import { useEffect } from "react";
import Cal, { getCalApi, type EmbedEvent } from "@calcom/embed-react";

type CalInlineEmbedProps = {
  calLink: string;
  config: Record<string, string>;
  namespace: string;
  className?: string;
  onBookingSuccess?: () => void;
  onLoadError?: () => void;
};

export default function CalInlineEmbed({
  calLink,
  className = "w-full",
  config,
  namespace,
  onBookingSuccess,
  onLoadError,
}: CalInlineEmbedProps) {
  useEffect(() => {
    let disposed = false;
    let calApi: Awaited<ReturnType<typeof getCalApi>> | null = null;

    const handleBookingSuccess = (
      _event: EmbedEvent<"bookingSuccessfulV2">,
    ) => {
      onBookingSuccess?.();
    };
    const handleLinkFailed = (_event: EmbedEvent<"linkFailed">) => {
      onLoadError?.();
    };

    getCalApi({ namespace })
      .then((api) => {
        if (disposed) return;

        calApi = api;
        api("on", {
          action: "bookingSuccessfulV2",
          callback: handleBookingSuccess,
        });
        api("on", { action: "linkFailed", callback: handleLinkFailed });
        api("ui", {
          hideEventTypeDetails: true,
          theme: "light",
          cssVarsPerTheme: {
            light: {
              "cal-brand": "#FFB37C",
              "cal-brand-emphasis": "#F49D66",
              "cal-brand-text": "#4D3434",
              "cal-text": "#4D3434",
              "cal-text-emphasis": "#315243",
              "cal-border": "#E9DED8",
              "cal-border-radius": "8px",
            },
            dark: {
              "cal-brand": "#FFB37C",
              "cal-brand-emphasis": "#F49D66",
              "cal-brand-text": "#4D3434",
              "cal-text": "#4D3434",
              "cal-text-emphasis": "#315243",
              "cal-border": "#E9DED8",
              "cal-border-radius": "8px",
            },
          },
        });
      })
      .catch(() => {
        if (!disposed) onLoadError?.();
      });

    return () => {
      disposed = true;
      calApi?.("off", {
        action: "bookingSuccessfulV2",
        callback: handleBookingSuccess,
      });
      calApi?.("off", { action: "linkFailed", callback: handleLinkFailed });
    };
  }, [namespace, onBookingSuccess, onLoadError]);

  return (
    <Cal
      namespace={namespace}
      calOrigin="https://cal.com"
      calLink={calLink}
      config={config}
      className={className}
    />
  );
}
